import React from 'react';
import Icon, { ICONS } from '@/components/common/Elements/Icon/Icon';

interface ActionButtonsProps {
  onRefresh: () => void;
  onToggleFullscreen: () => void;
  isFullscreen: boolean;
  sticky?: boolean;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  onRefresh,
  onToggleFullscreen,
  isFullscreen,
}) => {
  const iconStyle = isFullscreen ? { color: 'white' } : undefined;
  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    top: -4,
    right: 12,
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <div style={wrapperStyle}>
      <button
        onClick={onRefresh}
        style={{
          border: 'none',
          borderRadius: '50%',
          padding: 8,
          cursor: 'pointer',
          background: 'transparent',
        }}
        title="Refresh report"
        aria-label="Refresh report"
      >
        <Icon icon={ICONS.refresh} style={iconStyle} />
      </button>
      <button
        onClick={onToggleFullscreen}
        style={{
          border: 'none',
          borderRadius: '50%',
          padding: 8,
          cursor: 'pointer',
          background: 'transparent',
        }}
        title={isFullscreen ? 'Exit fullscreen' : 'View report fullscreen'}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
      >
        <Icon
          icon={isFullscreen ? ICONS.close_fullscreen : ICONS.open_in_full}
          style={iconStyle}
        />
      </button>
    </div>
  );
};

interface ErrorOverlayProps {
  errorMessage: string;
  iframeSrc: string;
  onRetry: () => void;
}

export const ErrorOverlay: React.FC<ErrorOverlayProps> = ({
  errorMessage,
  iframeSrc,
  onRetry,
}) => {
  return (
    <div>
      <div style={{ marginBottom: 12, color: '#b00020', fontWeight: 600 }}>
        {errorMessage}
      </div>
      <div style={{ marginBottom: 12, color: '#444', wordBreak: 'break-all' }}>
        {iframeSrc}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button
          onClick={() => window.open(iframeSrc, '_blank')}
          style={{ padding: '8px 12px', cursor: 'pointer' }}
        >
          Open in new tab
        </button>
        <button
          onClick={onRetry}
          style={{ padding: '8px 12px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    </div>
  );
};

interface LoadingOverlayProps {
  hasError: boolean;
  errorMessage?: string;
  iframeSrc?: string;
  onRetry?: () => void;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  hasError,
  errorMessage,
  iframeSrc,
  onRetry,
}) => {
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    zIndex: 9,
  };

  if (hasError && errorMessage && iframeSrc && onRetry) {
    return (
      <div
        style={{
          ...baseStyle,
          background: 'rgba(255,255,255,0.9)',
          flexDirection: 'column',
          padding: 16,
          textAlign: 'center',
        }}
      >
        <ErrorOverlay
          errorMessage={errorMessage}
          iframeSrc={iframeSrc}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        ...baseStyle,
        background: 'rgba(255,255,255,0.7)',
      }}
    >
      Initializing report...
    </div>
  );
};

interface ReportIframeProps {
  src: string;
  isGoogleDoc: boolean;
  isGoogleSheet: boolean;
  docScale: number;
  style?: React.CSSProperties;
  onLoad: () => void;
  onError: () => void;
  sandboxPermissions?: string;
  pdfPadding?: boolean; // optional vertical padding for PDF embeds
}

export const ReportIframe: React.FC<ReportIframeProps> = ({
  src,
  isGoogleDoc,
  isGoogleSheet,
  docScale,
  style,
  onLoad,
  onError,
  sandboxPermissions,
  pdfPadding = false,
}) => {
  const width = isGoogleDoc ? `${100 / docScale}%` : '100%';
  const height = isGoogleDoc ? `${100 / docScale}%` : '100%';
  const transform = isGoogleDoc ? `scale(${docScale})` : undefined;

  const sandbox = isGoogleDoc || isGoogleSheet ? undefined : sandboxPermissions;

  const iframeStyle: React.CSSProperties = {
    width,
    height,
    border: 'none',
    borderRadius: '24px',
    transform,
    paddingTop: '32px',
    paddingBottom: '32px',
    ...style,
  };

  if (pdfPadding) {
    const pdfIframeStyle: React.CSSProperties = {
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: 12,
      paddingTop: '32px',
      paddingBottom: '32px',
      boxSizing: 'border-box',
      ...style,
    };

    return (
      <div
        style={{
          paddingTop: 12,
          paddingBottom: 12,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <iframe
          frameBorder="0"
          style={pdfIframeStyle}
          src={src}
          allowFullScreen={true}
          loading="eager"
          sandbox={sandbox}
          onLoad={onLoad}
          onError={onError}
        />
      </div>
    );
  }

  return (
    <iframe
      frameBorder="none"
      style={iframeStyle}
      src={src}
      allowFullScreen={true}
      loading="eager"
      sandbox={sandbox}
      onLoad={onLoad}
      onError={onError}
    />
  );
};
